import { reaction } from "mobx";
import { LoginAnalyticsEvent } from "healthex-common/analytics.dtos";
import { FeatureFlag } from "healthex-common/features.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { LoginSource } from "healthex-common/heap.dtos";
import { StudyVersionStatus } from "healthex-common/study.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { LoginFlow } from "../Logic/AuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { effectiveQueryParams } from "./queryParams";
import { routeEquals } from "./RoutingUIManager";
import { generateOAuthState, getAnonymousUserId } from "../Logic/AuthManager";
import { buildHostedRedirectUrl, isHostedRedirectUriAllowed } from "./hostedRedirect";
export class HostedIdpSsoManager {
    constructor(persistence, routingUIManager, studyManager, authManager, featureManager, socialLoginAuthManager, patientService, analyticsService) {
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.authManager = authManager;
        this.featureManager = featureManager;
        this.socialLoginAuthManager = socialLoginAuthManager;
        this.patientService = patientService;
        this.analyticsService = analyticsService;
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if (routeEquals(currentRoute, NamedRoutes.PATIENT_CONSENT_IDP_CALLBACK)) {
                void this.handleCallback();
            }
        }, { fireImmediately: true });
    }
    async maybeStartSso(studyId, iss) {
        if (!this.featureManager.isFeatureEnabled(FeatureFlag.HOSTED_IDP_SSO_ENABLED)) {
            console.log("[HostedFlow] maybeStartSso aborted: HOSTED_IDP_SSO_ENABLED is not enabled for this org.");
            return;
        }
        if (this.authManager.isLoggedIn && !this.authManager.isHostedSsoRestricted) {
            console.log(`[HostedFlow] Skipping hosted-SSO redirect: device already has a full-scope session; proceeding with existing token.`);
            return;
        }
        const allowed = await this.studyManager.isIdpIssuerAllowedForStudy(studyId, iss);
        if (!allowed) {
            console.log(`[HostedFlow] iss validation failed: "${iss}" is not in the allowed IdP issuer list for study ${studyId}; clearing any existing iss from sessionStorage.`);
            this.persistence.setIss(null);
            return;
        }
        console.log(`[HostedFlow] iss validation succeeded: "${iss}" is allowed for study ${studyId}; persisting to sessionStorage.`);
        this.persistence.setIss(iss);
        this.persistence.setHostedSsoStudyId(studyId);
        await this.redirectToIdp(iss);
    }
    async redirectToIdp(iss) {
        var _a;
        const state = generateOAuthState();
        this.persistence.setHostedSsoState(state);
        await this.analyticsService.trackHeapEvent({
            event: LoginAnalyticsEvent.LOGIN_ATTEMPTED,
            user_id: (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined,
            properties: { ...this.buildHostedHeapProperties() },
        });
        const callbackUrl = `${window.location.origin}/#/patient-consent/idp-callback`;
        const authorizeUrl = new URL(iss);
        authorizeUrl.searchParams.set("state", state);
        authorizeUrl.searchParams.set("redirect_uri", callbackUrl);
        console.log(`[HostedFlow] Redirecting to IdpURL: ${authorizeUrl.toString()} with state: ${state}`);
        window.location.href = authorizeUrl.toString();
    }
    async handleCallback() {
        var _a;
        const queryParams = effectiveQueryParams();
        const code = queryParams.get("code");
        const incomingState = queryParams.get("state");
        const storedState = this.persistence.getHostedSsoState();
        const iss = this.persistence.getIss();
        const studyId = this.persistence.getHostedSsoStudyId();
        console.log(`Received IDP callback with state: ${incomingState} for the stored state: ${storedState}`);
        try {
            if (!code || !incomingState) {
                throw new Error("IdP callback missing code or state");
            }
            if (!storedState || incomingState !== storedState) {
                throw new Error(`IdP callback state mismatch (incoming=${incomingState}, stored=${storedState})`);
            }
            if (!iss || !studyId) {
                throw new Error("IdP callback missing iss or studyId from sessionStorage");
            }
            const result = await this.studyManager.exchangeHostedSsoCode(studyId, code, this.buildHostedHeapProperties());
            if (result.error === "CLEAR_ACCOUNT_LINKING_REQUIRED") {
                if (!result.linkingToken) {
                    throw new Error("CLEAR_ACCOUNT_LINKING_REQUIRED but no linkingToken returned");
                }
                console.log("[HostedFlow] Patient has only CLEAR auth; routing to CLEAR account-linking flow.");
                this.socialLoginAuthManager.enterAccountLinkingRequiredState(result.linkingToken, GFIDProvider.HOSTED_SSO);
            }
            else {
                if (!result.token) {
                    throw new Error(`Exchange returned no session token and no recognised linking signal (error: ${result.error})`);
                }
                console.log(`[HostedFlow] Backend code exchange succeeded for patient: ${result.patientId} with state: ${incomingState}`);
                await this.authManager.handleSocialLogin(result.token, LoginFlow.GFID_HOSTED_SSO, result.isFirstTimeLogin);
            }
            this.persistence.setHostedSsoState(null);
            this.persistence.setHostedSsoStudyId(null);
            const cleanedUrl = new URL(window.location.href);
            cleanedUrl.searchParams.delete("code");
            cleanedUrl.searchParams.delete("state");
            window.history.replaceState(null, "", cleanedUrl.toString());
            console.log(`[HostedFlow] Redirecting back to enrollment/link for study ${studyId} with state: ${incomingState}.`);
            this.routingUIManager.redirectTo(NamedRoutes.PATIENT_CONSENT_ENROLLMENT, { studyId, step: "link" });
        }
        catch (e) {
            console.log("[HostedFlow] Callback failed; redirecting partner with status=failed:", e);
            await this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined,
                properties: { ...this.buildHostedHeapProperties(), error: e instanceof Error ? e.message : String(e) },
            });
            await this.redirectToPartnerWithFailure(studyId);
        }
    }
    async redirectToPartnerWithFailure(studyId) {
        var _a;
        const redirectUri = this.persistence.getRedirectUriForHostedFlow();
        const xid = this.unsignedExternalId();
        if (!redirectUri || !studyId) {
            console.warn("[HostedFlow] Cannot redirect partner on failure: missing redirect URI or studyId.");
            this.persistence.clearHostedFlowSessionState();
        }
        else {
            let allowedUrls = null;
            try {
                const study = await this.studyManager.getStudyPublicInfo(studyId, StudyVersionStatus.ACTIVE);
                allowedUrls = (_a = study === null || study === void 0 ? void 0 : study.hostedRedirectUrls) !== null && _a !== void 0 ? _a : [];
            }
            catch (e) {
                console.warn("[HostedFlow] Could not load study to validate the failure redirect; leaving hosted-flow state for retry:", e);
            }
            if (allowedUrls !== null) {
                this.persistence.clearHostedFlowSessionState();
                if (isHostedRedirectUriAllowed(redirectUri, allowedUrls)) {
                    window.location.href = buildHostedRedirectUrl(redirectUri, { status: "failed", xid });
                }
                else {
                    console.warn("[HostedFlow] Failure redirect URI not in the study's allowed list; not redirecting:", redirectUri);
                }
            }
        }
    }
    unsignedExternalId() {
        const signed = this.persistence.getSignedExternalId();
        if (!signed)
            return null;
        const lastDot = signed.lastIndexOf(".");
        return lastDot > 0 ? signed.slice(0, lastDot) : null;
    }
    buildHostedHeapProperties() {
        return {
            ...getHeapGlobalEventProperties(),
            provider: GFIDProvider.HOSTED_SSO,
            source: LoginSource.CONSENT_LOGIN,
        };
    }
}
